import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Footer from '../../components/Footer';
import { saveAssessmentProgress, loadAssessmentProgress, completeAssessment, deleteAssessmentData } from '../../services/assessmentService';
import progressTrackingService from '../../services/progressTrackingService';

import QuestionCard from './components/QuestionCard';
import AttitudeAssessment from './components/AttitudeAssessment';
import QuestionnaireProgress from './components/QuestionnaireProgress';
import NavigationControls from './components/NavigationControls';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import AdSense from '../../components/AdSense';

const AdaptiveQuestionnaire = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, isPremium, loading } = useAuth();
  
  // Get assessment setup data from navigation state or load from database
  const [assessmentSetup, setAssessmentSetup] = useState(
    location?.state?.assessmentSetup || null
  );

  // Debug: Log the received assessment setup data
  console.log('AdaptiveQuestionnaire - Received assessmentSetup:', assessmentSetup);
  console.log('AdaptiveQuestionnaire - assessmentSetup.occupation type:', typeof assessmentSetup?.occupation);
  console.log('AdaptiveQuestionnaire - assessmentSetup.industry type:', typeof assessmentSetup?.industry);

  // Ensure all required properties exist with fallbacks and are strings
  const safeAssessmentSetup = {
    occupation: assessmentSetup?.occupation || '',
    seniority: assessmentSetup?.seniority || '',
    industry: assessmentSetup?.industry || ''
  };

  console.log('AdaptiveQuestionnaire - Safe assessment setup:', safeAssessmentSetup);

  // Check if this is a retake or continuing existing assessment
  const isRetake = location?.state?.isRetake || false;
  const isContinuingExisting = location?.state?.continueExisting || false;

  // Show loading state while authentication is being determined
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Show error if user is not authenticated
  if (!user && !userProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto" />
          <h2 className="text-xl font-semibold text-foreground">Authentication Required</h2>
          <p className="text-muted-foreground">Please sign in to access the assessment.</p>
          <Button onClick={() => navigate('/authentication')}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // Questionnaire state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [attitudes, setAttitudes] = useState({
    jobSatisfaction: 5,
    stress: 5,
    confidence: 5,
    momentum: 5
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load existing assessment data from database
  useEffect(() => {
  const loadExistingAssessment = async () => {
    if (!user) return;

    try {
      console.log('Loading assessment data for user:', user.id);
      // If continuing an existing assessment, pass the assessment ID
      const assessmentId = location?.state?.assessmentId || null;
      const result = await loadAssessmentProgress(user.id, assessmentId);
        if (result.success && result.data) {
          console.log('Loading existing assessment data:', result.data);
          console.log('Current question index from DB:', result.data.current_question_index);
          console.log('Answers from DB:', result.data.answers);
          
          // Load assessment setup from database if available
          if (result.data.assessment_setup) {
            console.log('Loading assessment setup from database:', result.data.assessment_setup);
            setAssessmentSetup(result.data.assessment_setup);
          }
          
          // Load answers and attitudes from database
          if (result.data.answers) {
            setAnswers(result.data.answers);
          }
          if (result.data.attitudes) {
            setAttitudes(result.data.attitudes);
          }
          if (result.data.current_question_index !== undefined) {
            setCurrentQuestionIndex(result.data.current_question_index);
            console.log('Set current question index to:', result.data.current_question_index);
          }
          
          setHasUnsavedChanges(false);
        } else {
          console.log('No existing assessment data found, starting fresh');
        }
      } catch (error) {
        console.error('Error loading assessment data:', error);
      }
    };

    loadExistingAssessment();
  }, [user]);

  // Clear previous answers if this is a retake
  useEffect(() => {
    const clearAssessmentData = async () => {
      if (isRetake && user) {
        console.log('Retake detected - clearing previous assessment data');
        
        // Clear database assessment data
        await deleteAssessmentData(user.id);
        
        // Clear local state
        setAnswers({});
        setAttitudes({
          jobSatisfaction: 5,
          stress: 5,
          confidence: 5,
          momentum: 5
        });
        setCurrentQuestionIndex(0);
        setHasUnsavedChanges(false);
      }
    };

    clearAssessmentData();
  }, [isRetake, user]);

  // Base questions for all users (first 10 questions)
  const baseQuestions = [
    {
      id: 1,
      type: 'multiple_choice',
      text: `As a ${safeAssessmentSetup.seniority} ${safeAssessmentSetup.occupation}, what is your primary career goal for the next 12 months?`,
      subtitle: 'Select the option that best describes your main professional objective',
      required: true,
      options: [
        {
          value: 'skill_development',
          label: 'Develop new technical skills',
          description: 'Focus on learning new technologies and improving technical expertise'
        },
        {
          value: 'leadership',
          label: 'Move into leadership roles',
          description: 'Transition to team lead, manager, or senior positions'
        },
        {
          value: 'specialization',
          label: 'Become a subject matter expert',
          description: 'Deep dive into a specific area or domain'
        },
        {
          value: 'career_change',
          label: 'Change career direction',
          description: 'Explore different roles or industries'
        }
      ]
    },
    {
      id: 2,
      type: 'slider',
      text: 'How would you rate your current technical skills relative to your role requirements?',
      subtitle: 'Consider both depth and breadth of your technical knowledge',
      required: true,
      min: 1,
      max: 5,
      minLabel: 'Needs Improvement',
      maxLabel: 'Expert Level',
      description: '1 = Significant gaps, 5 = Exceeds expectations'
    },
    {
      id: 3,
      type: 'checkbox',
      text: 'Which areas do you feel need the most improvement in your current role?',
      subtitle: 'Select all that apply - this helps us prioritize your development plan',
      required: true,
      options: [
        {
          value: 'technical_skills',
          label: 'Technical Skills',
          description: 'Programming, tools, frameworks, or domain expertise'
        },
        {
          value: 'communication',
          label: 'Communication',
          description: 'Written, verbal, presentation, or interpersonal skills'
        },
        {
          value: 'leadership',
          label: 'Leadership & Management',
          description: 'Team leadership, project management, or mentoring'
        },
        {
          value: 'problem_solving',
          label: 'Problem Solving',
          description: 'Analytical thinking, debugging, or strategic planning'
        },
        {
          value: 'time_management',
          label: 'Time Management',
          description: 'Prioritization, productivity, or work-life balance'
        }
      ]
    },
    {
      id: 4,
      type: 'multiple_choice',
      text: 'What is your preferred learning style for professional development?',
      subtitle: 'Understanding your learning preference helps us recommend the best resources',
      required: true,
      options: [
        {
          value: 'hands_on',
          label: 'Hands-on Practice',
          description: 'Learning by doing, building projects, and practical application'
        },
        {
          value: 'structured',
          label: 'Structured Courses',
          description: 'Online courses, certifications, and formal training programs'
        },
        {
          value: 'mentorship',
          label: 'Mentorship & Coaching',
          description: 'One-on-one guidance, feedback, and personalized advice'
        },
        {
          value: 'self_directed',
          label: 'Self-directed Research',
          description: 'Reading documentation, articles, and independent exploration'
        }
      ]
    },
    {
      id: 5,
      type: 'slider',
      text: 'How much time can you realistically dedicate to professional development per week?',
      subtitle: 'Be honest about your availability to ensure realistic goal setting',
      required: true,
      min: 1,
      max: 5,
      minLabel: '1-2 hours',
      maxLabel: '10+ hours',
      description: '1 = 1-2 hours, 2 = 3-4 hours, 3 = 5-6 hours, 4 = 7-9 hours, 5 = 10+ hours'
    },
    {
      id: 6,
      type: 'multiple_choice',
      text: 'What is your biggest challenge in advancing your career right now?',
      subtitle: 'Identifying your main obstacle helps us create targeted solutions',
      required: true,
      options: [
        {
          value: 'skill_gaps',
          label: 'Technical Skill Gaps',
          description: 'Missing key technical competencies for advancement'
        },
        {
          value: 'opportunities',
          label: 'Limited Opportunities',
          description: 'Lack of growth opportunities in current organization'
        },
        {
          value: 'networking',
          label: 'Professional Network',
          description: 'Need to build stronger professional relationships'
        },
        {
          value: 'confidence',
          label: 'Self-Confidence',
          description: 'Imposter syndrome or lack of confidence in abilities'
        },
        {
          value: 'direction',
          label: 'Career Direction',
          description: 'Uncertainty about next steps or career path'
        }
      ]
    },
    {
      id: 7,
      type: 'text',
      text: 'What is your current ZIP code?',
      subtitle: 'This helps us provide location-specific salary insights and local opportunities',
      required: true,
      placeholder: 'e.g., 10001',
      description: 'We use this to find local job markets, salary data, and networking opportunities in your area'
    },
    {
      id: 8,
      type: 'multiple_choice',
      text: 'Where would you like to work in the future?',
      subtitle: 'Understanding your location preferences helps us tailor opportunities',
      required: true,
      options: [
        {
          value: 'current_location',
          label: 'Stay in current location',
          description: 'Continue working in your current area'
        },
        {
          value: 'different_city',
          label: 'Different city (same country)',
          description: 'Open to relocating within your country'
        },
        {
          value: 'remote_only',
          label: 'Remote work only',
          description: 'Prefer fully remote opportunities'
        },
        {
          value: 'hybrid',
          label: 'Hybrid (remote + office)',
          description: 'Mix of remote and in-person work'
        },
        {
          value: 'international',
          label: 'International opportunities',
          description: 'Open to working in different countries'
        }
      ]
    },
    {
      id: 9,
      type: 'text',
      text: 'If you\'re open to relocating, what ZIP code or city interests you?',
      subtitle: 'Optional: Help us find opportunities in your target location',
      required: false,
      placeholder: 'e.g., 90210 or San Francisco, CA',
      description: 'Leave blank if you prefer to stay in your current location'
    },
    {
      id: 10,
      type: 'multiple_choice',
      text: 'What motivates you most in your professional life?',
      subtitle: 'Understanding your core motivations helps us align your development plan',
      required: true,
      options: [
        {
          value: 'financial_growth',
          label: 'Financial Growth',
          description: 'Salary increases, bonuses, and financial security'
        },
        {
          value: 'skill_mastery',
          label: 'Skill Mastery',
          description: 'Becoming an expert in your field and continuous learning'
        },
        {
          value: 'impact',
          label: 'Making an Impact',
          description: 'Contributing to meaningful projects and positive change'
        },
        {
          value: 'recognition',
          label: 'Recognition & Status',
          description: 'Industry recognition, awards, and professional reputation'
        },
        {
          value: 'work_life_balance',
          label: 'Work-Life Balance',
          description: 'Flexibility, time with family, and personal fulfillment'
        }
      ]
    }
  ];

  // Premium-only questions (questions 11-20)
  const premiumQuestions = [
    {
      id: 11,
      type: 'slider',
      text: 'How satisfied are you with your current work environment and company culture?',
      subtitle: 'Rate your overall satisfaction with your workplace',
      required: true,
      min: 1,
      max: 10,
      minLabel: 'Very Dissatisfied',
      maxLabel: 'Very Satisfied',
      description: '1 = Very dissatisfied, 10 = Very satisfied'
    },
    {
      id: 12,
      type: 'checkbox',
      text: 'Which soft skills would you like to develop further?',
      subtitle: 'Select all that apply - these are crucial for career advancement',
      required: true,
      options: [
        {
          value: 'emotional_intelligence',
          label: 'Emotional Intelligence',
          description: 'Self-awareness, empathy, and relationship management'
        },
        {
          value: 'critical_thinking',
          label: 'Critical Thinking',
          description: 'Analytical reasoning and problem-solving approaches'
        },
        {
          value: 'adaptability',
          label: 'Adaptability',
          description: 'Flexibility and resilience in changing environments'
        },
        {
          value: 'creativity',
          label: 'Creativity & Innovation',
          description: 'Thinking outside the box and generating new ideas'
        },
        {
          value: 'negotiation',
          label: 'Negotiation',
          description: 'Persuasion, conflict resolution, and deal-making'
        },
        {
          value: 'public_speaking',
          label: 'Public Speaking',
          description: 'Presentation skills and confident communication'
        }
      ]
    },
    {
      id: 13,
      type: 'multiple_choice',
      text: 'What type of feedback do you find most valuable for your growth?',
      subtitle: 'Understanding your feedback preferences helps us structure your development',
      required: true,
      options: [
        {
          value: 'constructive_criticism',
          label: 'Constructive Criticism',
          description: 'Direct feedback on areas for improvement'
        },
        {
          value: 'positive_reinforcement',
          label: 'Positive Reinforcement',
          description: 'Recognition of strengths and achievements'
        },
        {
          value: 'peer_feedback',
          label: 'Peer Feedback',
          description: 'Input from colleagues at similar levels'
        },
        {
          value: 'mentor_guidance',
          label: 'Mentor Guidance',
          description: 'Advice from experienced professionals'
        },
        {
          value: 'data_driven',
          label: 'Data-Driven Insights',
          description: 'Metrics and measurable performance indicators'
        }
      ]
    },
    {
      id: 14,
      type: 'slider',
      text: 'How comfortable are you with taking calculated risks in your career?',
      subtitle: 'Rate your willingness to step outside your comfort zone',
      required: true,
      min: 1,
      max: 10,
      minLabel: 'Very Risk-Averse',
      maxLabel: 'Very Risk-Taking',
      description: '1 = Prefer stability, 10 = Embrace new challenges'
    },
    {
      id: 15,
      type: 'multiple_choice',
      text: 'What is your preferred method for staying updated with industry trends?',
      subtitle: 'Understanding your information consumption habits',
      required: true,
      options: [
        {
          value: 'professional_networks',
          label: 'Professional Networks',
          description: 'LinkedIn, industry associations, and conferences'
        },
        {
          value: 'online_learning',
          label: 'Online Learning Platforms',
          description: 'Coursera, Udemy, and specialized courses'
        },
        {
          value: 'industry_publications',
          label: 'Industry Publications',
          description: 'Trade magazines, blogs, and research papers'
        },
        {
          value: 'mentorship',
          label: 'Mentorship & Coaching',
          description: 'Learning from experienced professionals'
        },
        {
          value: 'hands_on_experience',
          label: 'Hands-on Experience',
          description: 'Learning through projects and experimentation'
        }
      ]
    },
    {
      id: 16,
      type: 'text',
      text: 'Describe a recent professional challenge you overcame. What did you learn?',
      subtitle: 'Understanding your problem-solving approach and growth mindset',
      required: true,
      placeholder: 'Describe the challenge, your approach, and key learnings...',
      description: 'This helps us understand your resilience and learning patterns'
    },
    {
      id: 17,
      type: 'slider',
      text: 'How important is work-life balance to your career satisfaction?',
      subtitle: 'Rate the importance of maintaining personal time and flexibility',
      required: true,
      min: 1,
      max: 10,
      minLabel: 'Not Important',
      maxLabel: 'Extremely Important',
      description: '1 = Work is priority, 10 = Balance is crucial'
    },
    {
      id: 18,
      type: 'multiple_choice',
      text: 'What type of work environment brings out your best performance?',
      subtitle: 'Understanding your optimal working conditions',
      required: true,
      options: [
        {
          value: 'collaborative',
          label: 'Highly Collaborative',
          description: 'Team-based projects with frequent interaction'
        },
        {
          value: 'autonomous',
          label: 'Independent & Autonomous',
          description: 'Self-directed work with minimal supervision'
        },
        {
          value: 'structured',
          label: 'Structured & Organized',
          description: 'Clear processes, deadlines, and defined roles'
        },
        {
          value: 'dynamic',
          label: 'Dynamic & Fast-Paced',
          description: 'Rapid changes, multiple projects, and quick decisions'
        },
        {
          value: 'creative',
          label: 'Creative & Flexible',
          description: 'Open-ended projects with room for innovation'
        }
      ]
    },
    {
      id: 19,
      type: 'checkbox',
      text: 'Which areas of your industry are you most interested in exploring?',
      subtitle: 'Select all that apply - helps us identify growth opportunities',
      required: true,
      options: [
        {
          value: 'emerging_technologies',
          label: 'Emerging Technologies',
          description: 'AI, blockchain, IoT, and cutting-edge innovations'
        },
        {
          value: 'sustainability',
          label: 'Sustainability & ESG',
          description: 'Environmental, social, and governance initiatives'
        },
        {
          value: 'digital_transformation',
          label: 'Digital Transformation',
          description: 'Modernizing traditional processes and systems'
        },
        {
          value: 'data_analytics',
          label: 'Data Analytics & AI',
          description: 'Big data, machine learning, and business intelligence'
        },
        {
          value: 'cybersecurity',
          label: 'Cybersecurity',
          description: 'Information security and risk management'
        },
        {
          value: 'user_experience',
          label: 'User Experience & Design',
          description: 'UX/UI design and customer-centric approaches'
        }
      ]
    },
    {
      id: 20,
      type: 'text',
      text: 'What would you consider your biggest professional achievement so far?',
      subtitle: 'Understanding your proudest moments helps us build on your strengths',
      required: true,
      placeholder: 'Describe your achievement and why it matters to you...',
      description: 'This helps us understand what success looks like for you'
    }
  ];

  // Combine questions based on user tier
  const questions = isPremium 
    ? [...baseQuestions, ...premiumQuestions] 
    : baseQuestions;

  // Filter questions based on conditional logic
  const getVisibleQuestions = () => {
    return questions?.filter((question, index) => {
      // Always show first 6 questions (original questions)
      if (index < 6) return true;
      
      // For location questions (7, 8, 9), check conditions
      if (question?.id === 9) {
        // Show target location question only if user selected different_city or international
        const locationPreference = answers?.[8];
        return locationPreference === 'different_city' || locationPreference === 'international';
      }
      
      // Show all other questions (including premium questions if user is premium)
      return true;
    });
  };

  const visibleQuestions = getVisibleQuestions();
  const totalQuestions = visibleQuestions?.length + 1; // +1 for attitude assessment
  const currentQuestion = currentQuestionIndex + 1;
  const isAttitudeAssessment = currentQuestionIndex === visibleQuestions?.length;
  
  // Calculate completed questions based on current question index
  // If we're on question 2 (index 1), we've completed 1 question
  // If we're on the attitude assessment, we've completed all regular questions
  const completedQuestions = isAttitudeAssessment 
    ? visibleQuestions?.length + (isAttitudeComplete() ? 1 : 0)
    : currentQuestionIndex;


  function isAttitudeComplete() {
    return Object.values(attitudes)?.every(value => value >= 1 && value <= 10);
  }

  // Auto-save functionality
  useEffect(() => {
    if (user && (Object.keys(answers)?.length > 0 || hasUnsavedChanges)) {
      const saveTimer = setTimeout(() => {
        handleAutoSave();
      }, 2000);
      return () => clearTimeout(saveTimer);
    }
  }, [answers, attitudes, user]);

  const handleAutoSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      const result = await saveAssessmentProgress(user.id, {
        answers,
        attitudes,
        assessmentSetup: safeAssessmentSetup,
        currentQuestionIndex,
        isCompleted: false
      });

      if (result.success) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        console.log('Assessment progress saved successfully');
      } else {
        console.error('Failed to save assessment progress:', result.error);
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    setHasUnsavedChanges(true);
    
    // Clear error for this question
    if (errors?.[questionId]) {
      setErrors(prev => ({
        ...prev,
        [questionId]: null
      }));
    }
    
    // If this is question 8 (location preference), we might need to reset question 9
    if (questionId === 8 && (answer === 'current_location' || answer === 'remote_only' || answer === 'hybrid')) {
      setAnswers(prev => {
        const newAnswers = { ...prev };
        delete newAnswers[9]; // Clear target location answer
        return newAnswers;
      });
    }
  };

  const handleAttitudeChange = (newAttitudes) => {
    setAttitudes(newAttitudes);
    setHasUnsavedChanges(true);
  };

  const validateCurrentQuestion = () => {
    if (isAttitudeAssessment) {
      const attitudeErrors = {};
      Object.entries(attitudes)?.forEach(([key, value]) => {
        if (!value || value < 1 || value > 10) {
          attitudeErrors[key] = 'Please provide a rating between 1 and 10';
        }
      });
      
      if (Object.keys(attitudeErrors)?.length > 0) {
        setErrors(attitudeErrors);
        return false;
      }
      return true;
    }

    const currentQ = visibleQuestions?.[currentQuestionIndex];
    const answer = answers?.[currentQ?.id];
    
    if (currentQ?.required && (!answer || (Array.isArray(answer) && answer?.length === 0))) {
      setErrors({
        [currentQ?.id]: 'This question is required'
      });
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentQuestion()) return;
    
    // If we're on the attitude assessment, we can't go further (it's the last step)
    if (isAttitudeAssessment) {
      return;
    }
    
    // Move to next question or attitude assessment
    if (currentQuestionIndex < visibleQuestions?.length) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setErrors({});
    }
  };

  const handleComplete = async () => {
    if (!validateCurrentQuestion()) return;
    
    setIsLoading(true);
    try {
      // Complete the assessment and save to database
      const result = await completeAssessment(user.id, {
        answers,
        attitudes,
        assessmentSetup: {
          ...safeAssessmentSetup,
          currentZipCode: answers?.[7] || null,
          locationPreference: answers?.[8] || null,
          targetLocation: answers?.[9] || null
        }
      });

      if (result.success) {
        console.log('Assessment completed and saved successfully');
        
        // Mark BOTH assessment and questionnaire as completed in progress tracking
        try {
          await progressTrackingService.markAssessmentCompleted(user.id);
          await progressTrackingService.markQuestionnaireCompleted(user.id);
          console.log('Assessment and questionnaire marked as completed');
        } catch (error) {
          console.error('Error marking assessment/questionnaire as completed:', error);
        }
        
        // Navigate to AI plan generation with the saved data
        navigate('/ai-plan-generation', {
          state: {
            assessmentSetup: {
              ...safeAssessmentSetup,
              currentZipCode: answers?.[7] || null,
              locationPreference: answers?.[8] || null,
              targetLocation: answers?.[9] || null
            },
            answers,
            attitudes,
            completedAt: new Date()?.toISOString(),
            isRetake: isRetake,
            careerPlanId: result.data.careerPlan.id
          }
        });
      } else {
        console.error('Failed to complete assessment:', result.error);
        // Still navigate but show error
        navigate('/ai-plan-generation', {
          state: {
            assessmentSetup: {
              ...safeAssessmentSetup,
              currentZipCode: answers?.[7] || null,
              locationPreference: answers?.[8] || null,
              targetLocation: answers?.[9] || null
            },
            answers,
            attitudes,
            completedAt: new Date()?.toISOString(),
            isRetake: isRetake,
            error: result.error
          }
        });
      }
    } catch (error) {
      console.error('Failed to complete assessment:', error);
      // Still navigate but show error
      navigate('/ai-plan-generation', {
        state: {
          assessmentSetup: {
            ...safeAssessmentSetup,
            currentZipCode: answers?.[7] || null,
            locationPreference: answers?.[8] || null,
            targetLocation: answers?.[9] || null
          },
          answers,
          attitudes,
          completedAt: new Date()?.toISOString(),
          isRetake: isRetake,
          error: error.message
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthRequired = () => {
    navigate('/authentication');
  };

  const handleLogout = () => {
    // Handle logout logic
    navigate('/');
  };

  const canGoNext = () => {
    if (isAttitudeAssessment) {
      return isAttitudeComplete();
    }
    const currentQ = visibleQuestions?.[currentQuestionIndex];
    const answer = answers?.[currentQ?.id];
    return !currentQ?.required || (answer && (!Array.isArray(answer) || answer?.length > 0));
  };

  const canGoBack = currentQuestionIndex > 0;
  const isLastQuestion = isAttitudeAssessment;

  return (
    <>
      <Helmet>
        <title>Adaptive Questionnaire - CareerLift</title>
        <meta name="description" content="Complete your personalized adaptive questionnaire to receive tailored career guidance and development recommendations." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header onAuthRequired={handleAuthRequired} onLogout={handleLogout} />
        
        <main className="flex-1 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="text-center mb-2">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="ClipboardList" size={32} className="text-primary" />
                <h1 className="text-3xl font-bold text-foreground">
                  {isRetake ? 'Retake Career Assessment' : 'Career Assessment'}
                </h1>
              </div>
              <div className="flex justify-center mb-2">
                <audio
                  src="/assets/audio/Questionnaire.mp3"
                  controls
                  preload="metadata"
                  className="h-8 max-w-xs"
                />
              </div>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {isRetake 
                  ? 'Complete this updated questionnaire based on your new role selection to receive refreshed AI-powered career development recommendations.'
                  : 'Complete this personalized questionnaire to receive AI-powered career development recommendations tailored to your role and goals.'
                }
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {safeAssessmentSetup.seniority} {safeAssessmentSetup.occupation}
              </p>
              
              {/* AdSense - Banner for Free Users */}
              {!isPremium && (
                <div className="mt-6 mb-4">
                  <AdSense 
                    adSlot="1234567890"
                    adFormat="auto"
                    className="w-full"
                  />
                </div>
              )}
              
              {isRetake && (
                <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-lg inline-block">
                  <p className="text-sm text-primary font-medium">
                    🔄 Assessment Retake - Your previous responses have been cleared
                  </p>
                </div>
              )}
            </div>

            {/* Assessment Tier Indicator */}
            <div className="mb-2">
              {isPremium ? (
                <div className="bg-gradient-to-r from-warning/10 to-primary/10 border border-warning/20 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-3">
                    <Icon name="Crown" size={20} className="text-warning" />
                    <div className="text-center">
                      <h3 className="font-semibold text-warning">Premium Assessment</h3>
                      <p className="text-sm text-muted-foreground">
                        Comprehensive 20-question assessment for detailed career insights
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-secondary/10 to-primary/10 border border-secondary/20 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-3">
                    <Icon name="User" size={20} className="text-secondary" />
                    <div className="text-center">
                      <h3 className="font-semibold text-secondary">Free Assessment</h3>
                      <p className="text-sm text-muted-foreground">
                        10-question assessment • <span className="text-warning font-medium">Upgrade to Premium</span> for 20 detailed questions
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

              {isContinuingExisting && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-950/20 dark:border-blue-800/30">
                  <div className="flex items-center space-x-3">
                    <Icon name="Clock" size={20} className="text-blue-600 dark:text-blue-400" />
                    <div>
                      <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Continuing Your Assessment
                      </h3>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        We've loaded your previous progress. You can continue from where you left off or start over.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                {/* Current Question */}
                {isAttitudeAssessment ? (
                  <AttitudeAssessment
                    attitudes={attitudes}
                    onAttitudeChange={handleAttitudeChange}
                    errors={errors}
                  />
                ) : (
                  <QuestionCard
                    question={visibleQuestions?.[currentQuestionIndex]}
                    answer={answers?.[visibleQuestions?.[currentQuestionIndex]?.id]}
                    onAnswerChange={(answer) => handleAnswerChange(visibleQuestions?.[currentQuestionIndex]?.id, answer)}
                    error={errors?.[visibleQuestions?.[currentQuestionIndex]?.id]}
                  />
                )}

                {/* Navigation Controls */}
                <NavigationControls
                  currentQuestion={currentQuestion}
                  totalQuestions={totalQuestions}
                  canGoBack={canGoBack}
                  canGoNext={canGoNext()}
                  isLastQuestion={isLastQuestion}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  onComplete={handleComplete}
                  loading={isLoading}
                />
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* AdSense - visible for free users */}
                {!isPremium && (
                  <div className="">
                    <AdSense adSlot="1234567002" adFormat="auto" adStyle={{ display: 'block' }} />
                  </div>
                )}
                {/* Progress */}
                <QuestionnaireProgress
                  currentQuestion={currentQuestion}
                  totalQuestions={totalQuestions}
                  completedQuestions={completedQuestions}
                />

                {/* Assessment Info */}
                <div className="bg-card rounded-lg border border-border p-6 space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <Icon name="User" size={18} className="text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Assessment Details</h3>
                    </div>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Role:</span>
                      <span className="font-medium text-foreground">
                        {safeAssessmentSetup.occupation || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Level:</span>
                      <span className="font-medium text-foreground">
                        {safeAssessmentSetup.seniority || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Industry:</span>
                      <span className="font-medium text-foreground">
                        {safeAssessmentSetup.industry || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="font-medium text-foreground">8-12 minutes</span>
                    </div>
                  </div>
                </div>

                {/* Auto-save Status */}
                <div className="bg-card rounded-lg border border-border p-4">
                  <AutoSaveIndicator
                    isAuthenticated={!!user}
                    lastSaved={lastSaved}
                    isSaving={isSaving}
                    hasUnsavedChanges={hasUnsavedChanges}
                  />
                </div>

                {/* Help */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="HelpCircle" size={16} className="text-primary" />
                    <span className="font-medium text-foreground">Need Help?</span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>• Answer honestly for the most accurate recommendations</p>
                    <p>• You can go back to change previous answers</p>
                    <p>• Your progress is automatically saved</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default AdaptiveQuestionnaire;